import React, { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOffer, useDeleteOffer } from "@/hooks/use-offers";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/Card";
import { formatCurrency, formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { ExternalLink, Trash2, Link2, Users, Globe, Settings, Image, EyeOff, Copy, Crosshair, DollarSign, ShieldAlert, Filter, Pencil, Check, X as XIcon } from "lucide-react";
import { OfferUrlsTab } from "./tabs/OfferUrlsTab";
import { OfferAffiliatesTab } from "./tabs/OfferAffiliatesTab";
import { TrackingLinksTab } from "./tabs/TrackingLinksTab";
import { OfferCreativesTab } from "./tabs/OfferCreativesTab";
import { OfferVisibilityTab } from "./tabs/OfferVisibilityTab";
import { OfferCustomCapsTab } from "./tabs/OfferCustomCapsTab";
import { OfferScrubRatesTab } from "./tabs/OfferScrubRatesTab";
import { OfferTargetingTab } from "./tabs/OfferTargetingTab";
import { OfferCustomPayoutsTab } from "./tabs/OfferCustomPayoutsTab";
import { OfferPayoutsTab } from "./tabs/OfferPayoutsTab";
import { OfferTiersTab } from "./tabs/OfferTiersTab";
import { Banknote, Star } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { MacroPicker } from "@/components/MacroPicker";

interface TabItem {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface TabGroup {
  label: string;
  items: TabItem[];
}

const tabGroups: TabGroup[] = [
  {
    label: "Core",
    items: [
      { id: "details", label: "Details", icon: Settings },
      { id: "default-payouts", label: "Payouts", icon: Banknote },
      { id: "urls", label: "Offer URLs", icon: Globe },
    ],
  },
  {
    label: "Partners",
    items: [
      { id: "affiliates", label: "Affiliates", icon: Users },
      { id: "visibility", label: "Visibility", icon: EyeOff },
      { id: "creatives", label: "Creatives", icon: Image },
      { id: "tiers", label: "Tiers", icon: Star },
    ],
  },
  {
    label: "Controls",
    items: [
      { id: "caps", label: "Custom Caps", icon: ShieldAlert },
      { id: "scrub", label: "Scrub Rates", icon: Filter },
      { id: "targeting", label: "Targeting", icon: Crosshair },
      { id: "payouts", label: "Custom Payouts", icon: DollarSign },
    ],
  },
  {
    label: "Tools",
    items: [
      { id: "tracking", label: "Tracking Links", icon: Link2 },
    ],
  },
];

type TabId = string;

export function OfferDetailPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const { data, isLoading } = useOffer(id!);
  const deleteMutation = useDeleteOffer();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>("details");

  const copyMutation = useMutation({
    mutationFn: async () => api.post(`/v1/offers/${id}/copy`, {}),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["offers"] });
      navigate({ to: `/offers/${res.data.id}` });
    },
  });

  const offer = data?.data;

  async function handleDelete() {
    if (!offer) return;
    if (!window.confirm(`Are you sure you want to delete "${offer.name}"?`)) return;
    await deleteMutation.mutateAsync(offer.id);
    navigate({ to: "/offers" });
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Loading..." backHref="/offers" />
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-gray-100 rounded-xl" />
          <div className="h-32 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div>
        <PageHeader title="Offer Not Found" backHref="/offers" />
        <p className="text-gray-500">This offer does not exist or has been removed.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <PageHeader
          title={offer.name}
          description={`Offer #${offer.id}`}
          backHref="/offers"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => copyMutation.mutate()}
            disabled={copyMutation.isPending}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Copy className="w-4 h-4" />
            {copyMutation.isPending ? "Copying..." : "Duplicate"}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Vertical tab sidebar */}
        <nav className="w-48 shrink-0">
          <div className="sticky top-6 space-y-4">
            {tabGroups.map((group) => (
              <div key={group.label}>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 mb-1">{group.label}</p>
                <div className="flex flex-col gap-0.5">
                  {group.items.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={cn(
                          "flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm transition-colors text-left",
                          isActive
                            ? "bg-gray-900 text-white font-medium"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
                        )}
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* Tab content */}
        <div className="flex-1 min-w-0">
          {activeTab === "details" && <DetailsTab offer={offer} />}
          {activeTab === "default-payouts" && <OfferPayoutsTab offerId={id!} />}
          {activeTab === "urls" && <OfferUrlsTab offerId={id!} />}
          {activeTab === "affiliates" && <OfferAffiliatesTab offerId={id!} />}
          {activeTab === "creatives" && <OfferCreativesTab offerId={id!} />}
          {activeTab === "visibility" && <OfferVisibilityTab offerId={id!} />}
          {activeTab === "caps" && <OfferCustomCapsTab offerId={id!} />}
          {activeTab === "scrub" && <OfferScrubRatesTab offerId={id!} />}
          {activeTab === "targeting" && <OfferTargetingTab offerId={id!} />}
          {activeTab === "payouts" && <OfferCustomPayoutsTab offerId={id!} />}
          {activeTab === "tiers" && <OfferTiersTab offerId={id!} />}
          {activeTab === "tracking" && <TrackingLinksTab offerId={id!} />}
        </div>
      </div>
    </div>
  );
}

function DetailsTab({ offer }: { offer: Record<string, any> }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  const [error, setError] = useState("");

  const { data: advData } = useQuery({
    queryKey: ["advertisers"],
    queryFn: async () => {
      const res = await api.get("/v1/advertisers?pageSize=500");
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const { data: catData } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const res = await api.get("/v1/categories");
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const advertisers = advData?.data ?? [];
  const categories = catData?.data ?? [];

  const updateMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      const res = await api.patch(`/v1/offers/${offer.id}`, payload);
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || "Failed to update offer");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offer", String(offer.id)] });
      queryClient.invalidateQueries({ queryKey: ["offers"] });
      setEditing(false);
      setError("");
    },
    onError: (err: any) => setError(err.message),
  });

  function startEditing() {
    setForm({
      name: offer.name || "",
      status: offer.status || "active",
      visibility: offer.visibility || "public",
      advertiserId: offer.advertiserId || "",
      categoryId: offer.categoryId || "",
      currencyId: offer.currencyId || "USD",
      destinationUrl: offer.destinationUrl || "",
      previewUrl: offer.previewUrl || "",
      description: offer.description || "",
      termsAndConditions: offer.termsAndConditions || "",
      redirectMode: offer.redirectMode || "standard",
      conversionMethod: offer.conversionMethod || "server_postback",
      sessionDurationHours: offer.sessionDurationHours || 24,
      attributionMethod: offer.attributionMethod || "last_touch",
      isCapsEnabled: offer.isCapsEnabled || false,
      dailyConversionCap: offer.dailyConversionCap || 0,
      weeklyConversionCap: offer.weeklyConversionCap || 0,
      monthlyConversionCap: offer.monthlyConversionCap || 0,
      globalConversionCap: offer.globalConversionCap || 0,
      dailyClickCap: offer.dailyClickCap || 0,
      weeklyClickCap: offer.weeklyClickCap || 0,
      monthlyClickCap: offer.monthlyClickCap || 0,
      globalClickCap: offer.globalClickCap || 0,
      isAllowDuplicateConversion: offer.isAllowDuplicateConversion ?? true,
      isFailTrafficEnabled: offer.isFailTrafficEnabled || false,
    });
    setEditing(true);
    setError("");
  }

  function handleSave() {
    const payload: Record<string, any> = { ...form };
    if (payload.advertiserId) payload.advertiserId = Number(payload.advertiserId);
    if (payload.categoryId) payload.categoryId = Number(payload.categoryId);
    else payload.categoryId = null;
    payload.sessionDurationHours = Number(payload.sessionDurationHours);
    payload.dailyConversionCap = Number(payload.dailyConversionCap);
    payload.weeklyConversionCap = Number(payload.weeklyConversionCap);
    payload.monthlyConversionCap = Number(payload.monthlyConversionCap);
    payload.globalConversionCap = Number(payload.globalConversionCap);
    payload.dailyClickCap = Number(payload.dailyClickCap);
    payload.weeklyClickCap = Number(payload.weeklyClickCap);
    payload.monthlyClickCap = Number(payload.monthlyClickCap);
    payload.globalClickCap = Number(payload.globalClickCap);
    updateMutation.mutate(payload);
  }

  function set(key: string, value: any) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
  const selectCls = inputCls;
  const labelCls = "block text-xs font-medium text-gray-500 uppercase mb-1";

  if (editing) {
    return (
      <div className="space-y-6">
        {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}

        <Card title="General">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelCls}>Name</label>
              <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={selectCls}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Visibility</label>
              <select value={form.visibility} onChange={(e) => set("visibility", e.target.value)} className={selectCls}>
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="require_approval">Require Approval</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Advertiser</label>
              <select value={form.advertiserId} onChange={(e) => set("advertiserId", e.target.value)} className={selectCls}>
                <option value="">Select...</option>
                {advertisers.map((a: any) => (
                  <option key={a.id} value={a.id}>{a.name} (#{a.id})</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select value={form.categoryId || ""} onChange={(e) => set("categoryId", e.target.value)} className={selectCls}>
                <option value="">None</option>
                {categories.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Currency</label>
              <select value={form.currencyId} onChange={(e) => set("currencyId", e.target.value)} className={selectCls}>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
                <option value="JPY">JPY</option>
                <option value="INR">INR</option>
              </select>
            </div>
          </div>
        </Card>

        <Card title="URLs">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls + " mb-0"}>Destination URL</label>
                <MacroPicker onInsert={(macro) => set("destinationUrl", form.destinationUrl + macro)} />
              </div>
              <input type="text" value={form.destinationUrl} onChange={(e) => set("destinationUrl", e.target.value)} className={inputCls + " font-mono"} placeholder="https://example.com?tid={transaction_id}" />
            </div>
            <div>
              <label className={labelCls}>Preview URL</label>
              <input type="url" value={form.previewUrl} onChange={(e) => set("previewUrl", e.target.value)} className={inputCls} placeholder="https://..." />
            </div>
          </div>
        </Card>

        <Card title="Description & Terms">
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Description</label>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Terms & Conditions</label>
              <textarea value={form.termsAndConditions} onChange={(e) => set("termsAndConditions", e.target.value)} rows={3} className={inputCls} />
            </div>
          </div>
        </Card>

        <Card title="Tracking Configuration">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Redirect Mode</label>
              <select value={form.redirectMode} onChange={(e) => set("redirectMode", e.target.value)} className={selectCls}>
                <option value="standard">Standard</option>
                <option value="meta_refresh">Meta Refresh</option>
                <option value="double_meta_refresh">Double Meta Refresh</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Conversion Method</label>
              <select value={form.conversionMethod} onChange={(e) => set("conversionMethod", e.target.value)} className={selectCls}>
                <option value="server_postback">Server Postback</option>
                <option value="https_pixel">HTTPS Pixel</option>
                <option value="javascript">JavaScript</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Session Duration (hours)</label>
              <input type="number" min="1" max="8760" value={form.sessionDurationHours} onChange={(e) => set("sessionDurationHours", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Attribution Method</label>
              <select value={form.attributionMethod} onChange={(e) => set("attributionMethod", e.target.value)} className={selectCls}>
                <option value="last_touch">Last Touch</option>
                <option value="first_touch">First Touch</option>
              </select>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <input type="checkbox" id="allowDupe" checked={form.isAllowDuplicateConversion} onChange={(e) => set("isAllowDuplicateConversion", e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
              <label htmlFor="allowDupe" className="text-sm text-gray-700">Allow duplicate conversions</label>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <input type="checkbox" id="failTraffic" checked={form.isFailTrafficEnabled} onChange={(e) => set("isFailTrafficEnabled", e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
              <label htmlFor="failTraffic" className="text-sm text-gray-700">Enable fail traffic redirect</label>
            </div>
          </div>
        </Card>

        <Card title="Caps">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="capsEnabled" checked={form.isCapsEnabled} onChange={(e) => set("isCapsEnabled", e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
              <label htmlFor="capsEnabled" className="text-sm font-medium text-gray-700">Enable Caps</label>
            </div>
            {form.isCapsEnabled && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className={labelCls}>Daily Conv.</label>
                  <input type="number" min="0" value={form.dailyConversionCap} onChange={(e) => set("dailyConversionCap", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Weekly Conv.</label>
                  <input type="number" min="0" value={form.weeklyConversionCap} onChange={(e) => set("weeklyConversionCap", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Monthly Conv.</label>
                  <input type="number" min="0" value={form.monthlyConversionCap} onChange={(e) => set("monthlyConversionCap", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Global Conv.</label>
                  <input type="number" min="0" value={form.globalConversionCap} onChange={(e) => set("globalConversionCap", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Daily Clicks</label>
                  <input type="number" min="0" value={form.dailyClickCap} onChange={(e) => set("dailyClickCap", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Weekly Clicks</label>
                  <input type="number" min="0" value={form.weeklyClickCap} onChange={(e) => set("weeklyClickCap", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Monthly Clicks</label>
                  <input type="number" min="0" value={form.monthlyClickCap} onChange={(e) => set("monthlyClickCap", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Global Clicks</label>
                  <input type="number" min="0" value={form.globalClickCap} onChange={(e) => set("globalClickCap", e.target.value)} className={inputCls} />
                </div>
              </div>
            )}
          </div>
        </Card>

        <div className="flex items-center justify-end gap-3 pt-2 pb-6">
          <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <span className="inline-flex items-center gap-1.5"><XIcon className="w-4 h-4" /> Cancel</span>
          </button>
          <button onClick={handleSave} disabled={updateMutation.isPending} className="px-5 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" /> {updateMutation.isPending ? "Saving..." : "Save Changes"}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card title="Details" action={
          <button onClick={startEditing} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        }>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Status</dt>
              <dd className="mt-1"><StatusBadge status={offer.status} /></dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Visibility</dt>
              <dd className="mt-1"><StatusBadge status={offer.visibility} /></dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Advertiser ID</dt>
              <dd className="mt-1 text-sm text-gray-700">#{offer.advertiserId}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Category</dt>
              <dd className="mt-1 text-sm text-gray-700">{offer.categoryId ? `#${offer.categoryId}` : "None"}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Currency</dt>
              <dd className="mt-1 text-sm text-gray-700">{offer.currencyId}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Redirect Mode</dt>
              <dd className="mt-1 text-sm text-gray-700 capitalize">{offer.redirectMode?.replace(/_/g, " ")}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Conversion Method</dt>
              <dd className="mt-1 text-sm text-gray-700 capitalize">{offer.conversionMethod?.replace(/_/g, " ")}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Session Duration</dt>
              <dd className="mt-1 text-sm text-gray-700">{offer.sessionDurationHours}h</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Attribution</dt>
              <dd className="mt-1 text-sm text-gray-700 capitalize">{offer.attributionMethod?.replace(/_/g, " ")}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Created</dt>
              <dd className="mt-1 text-sm text-gray-700">{formatDate(offer.createdAt)}</dd>
            </div>
          </dl>
        </Card>

        <Card title="Destination URL">
          <a href={offer.destinationUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 break-all">
            {offer.destinationUrl}
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          </a>
          {offer.previewUrl && (
            <div className="mt-3">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Preview URL</p>
              <a href={offer.previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 break-all">
                {offer.previewUrl}
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              </a>
            </div>
          )}
        </Card>

        {offer.description && (
          <Card title="Description">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{offer.description}</p>
          </Card>
        )}

        {offer.termsAndConditions && (
          <Card title="Terms & Conditions">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{offer.termsAndConditions}</p>
          </Card>
        )}
      </div>

      <div className="space-y-6">
        <Card title="Tracking Options">
          <dl className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Duplicate Conversions</span>
              <span className="font-medium">{offer.isAllowDuplicateConversion ? "Allowed" : "Blocked"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Fail Traffic</span>
              <span className="font-medium">{offer.isFailTrafficEnabled ? "Enabled" : "Disabled"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Block Proxy</span>
              <span className="font-medium">{offer.isBlockProxy ? "Yes" : "No"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Deep Link</span>
              <span className="font-medium">{offer.isAllowDeepLink ? "Allowed" : "No"}</span>
            </div>
          </dl>
        </Card>

        {offer.isCapsEnabled && (
          <Card title="Caps">
            <dl className="space-y-3">
              {offer.dailyConversionCap > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Daily Conv.</span>
                  <span className="font-medium">{offer.dailyConversionCap}</span>
                </div>
              )}
              {offer.weeklyConversionCap > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Weekly Conv.</span>
                  <span className="font-medium">{offer.weeklyConversionCap}</span>
                </div>
              )}
              {offer.monthlyConversionCap > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Monthly Conv.</span>
                  <span className="font-medium">{offer.monthlyConversionCap}</span>
                </div>
              )}
              {offer.globalConversionCap > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Global Conv.</span>
                  <span className="font-medium">{offer.globalConversionCap}</span>
                </div>
              )}
              {offer.dailyClickCap > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Daily Clicks</span>
                  <span className="font-medium">{offer.dailyClickCap}</span>
                </div>
              )}
              {offer.weeklyClickCap > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Weekly Clicks</span>
                  <span className="font-medium">{offer.weeklyClickCap}</span>
                </div>
              )}
              {offer.monthlyClickCap > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Monthly Clicks</span>
                  <span className="font-medium">{offer.monthlyClickCap}</span>
                </div>
              )}
              {offer.globalClickCap > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Global Clicks</span>
                  <span className="font-medium">{offer.globalClickCap}</span>
                </div>
              )}
            </dl>
          </Card>
        )}

        {offer.payoutRevenues && offer.payoutRevenues.length > 0 && (
          <Card title="Payout / Revenue">
            <div className="space-y-3">
              {offer.payoutRevenues.map((pr: { id: number; entryName: string; payoutType: string; payoutAmount: number; revenueType: string; revenueAmount: number; isDefault: boolean }) => (
                <div key={pr.id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{pr.entryName}</span>
                    {pr.isDefault && (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Default</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Payout:</span>{" "}
                      <span className="font-medium">{formatCurrency(pr.payoutAmount)} ({(pr.payoutType ?? "").toUpperCase()})</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Revenue:</span>{" "}
                      <span className="font-medium">{formatCurrency(pr.revenueAmount)} ({(pr.revenueType ?? "").toUpperCase()})</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
